import { instanceBo } from "../utils/axios.create";
import { IAction } from "../domain/action.interface";

export interface ActionRequest {
	code: string;
	name: string;
	description: string;
	type: string;
	link?: string;
	order: number;
	// Child action ids. The backend stores these on the parent (Action.subActions)
	// and the app menu renders them as the item's dropdown children
	// (actionToMenu in user.store). SIMS-6516.
	subActions?: string[];
	createdBy: string;
}

export interface ActionUpdateRequest extends Omit<ActionRequest, "createdBy"> {
	_id: string;
	updatedBy: string;
}

export interface ActionsResponse {
	// backend-v2 BaseResponse returns statusCode as a string ("OK"); v1 used a numeric
	// 200. Widened for the v2 repoint (SIMS-6514). Consumers read body.* not statusCode.
	statusCode: number | string;
	body: {
		data: IAction[];
		pagination: {
			currentPage: number;
			pageSize: number;
			totalCount: number;
			totalPages: number;
		};
	};
}

export const getActions = async (
	search?: string,
	pageNumber?: number,
	pageSize?: number,
): Promise<ActionsResponse> => {
	const params = new URLSearchParams();
	if (search) params.append("search", search);
	if (pageNumber) params.append("pageNumber", pageNumber.toString());
	if (pageSize) params.append("pageSize", pageSize.toString());

	const queryString = params.toString();
	const url = `/actions${queryString ? `?${queryString}` : ""}`;

	// Repointed to backend-v2 (instanceBo) — SIMS-6514. The /v1 prefix rides on
	// REACT_APP_BACKEND_URL_BO; the response envelope shape is unchanged.
	return instanceBo
		.get(url)
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

export const getAllActions = async (): Promise<ActionsResponse> => {
	// Get all actions by setting a high page size (1000 should be more than enough)
	const params = new URLSearchParams();
	params.append("pageSize", "1000");

	const url = `/actions?${params.toString()}`;

	// Repointed to backend-v2 (instanceBo) — SIMS-6514.
	return instanceBo
		.get(url)
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

export const createAction = async (
	actionData: ActionRequest,
): Promise<IAction> => {
	// Repointed to backend-v2 (instanceBo) — SIMS-6516/6521/6527
	return instanceBo
		.post("/actions", actionData)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

export const updateAction = async (
	actionData: ActionUpdateRequest,
): Promise<IAction> => {
	const { _id, ...updateData } = actionData;
	// createdBy is immutable on the v2 backend: UpdateActionDto omits it and the
	// global ValidationPipe runs with forbidNonWhitelisted, so a stray createdBy
	// in the body triggers a 400. It can leak in via a spread of the create-shaped
	// form state, so strip it defensively here. updatedBy stays (backend requires it).
	delete (updateData as { createdBy?: string }).createdBy;
	// Repointed to backend-v2 (instanceBo) — SIMS-6516/6521/6527
	return instanceBo
		.put(`/actions/${_id}`, updateData)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

export const deleteAction = async (actionId: string): Promise<void> => {
	// Repointed to backend-v2 (instanceBo) — SIMS-6516/6521/6527
	return instanceBo
		.delete(`/actions/${actionId}`)
		.then(() => undefined)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};
